import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

import { connect } from "react-redux";

import { UserIcon, DocumentsList } from "components";
import {
  Select,
  Input,
  DatePicker,
  Button,
  Modal,
  Spin,
  notification,
  Icon,
  Pagination,
} from "antd";
import { projectService, noteService } from "services";
import { selectProps, dateTimeFormat, datePickerProps, LastProject } from "utils";

const NOTE_TYPE_LOG = 3;

export const ProjectLog = (props) => {
  const { user, fixedProject } = props;
  const canChangeProject = !fixedProject;

  let baseFilters = { user_name: `${user.name} ${user.surname}` },
    baseProject;

  if (canChangeProject) {
    const tmp = LastProject.get();
    if (tmp && !isNaN(tmp)) {
      baseFilters.project_id = +tmp;
      baseProject = +tmp;
    }
  }

  // Datas
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);

  // Filters
  const [filters, setFilters] = useState({ ...baseFilters });
  const [filtersUpdated, setFiltersUpdated] = useState(false);

  // Pagination
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // States
  const [creation, setCreation] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newLog, setNewLog] = useState(
    canChangeProject ? { project_id: baseProject || null } : { project_id: fixedProject?.id },
  );

  // Refs
  const doclist = useRef(null);

  // Fetchers
  const fetchLogs = async (currentFilters = filters) => {
    if (!currentFilters.project_id) delete currentFilters.project_id;

    const logs = await noteService.list({
      type_id: NOTE_TYPE_LOG,
      order_by: "date",
      order_direction: "DESC",
      page_number: pageNumber,
      per_page: pageSize,
      ...currentFilters,
      ...(fixedProject?.id && { project_id: fixedProject.id }),
    });

    setTotal(logs.total);
    setLogs(logs.data || logs);
  };

  const fetchProjects = async () => {
    if (!canChangeProject) return;
    const projects = await projectService.list({
      order_by: "code",
      order_direction: "ASC",
      disabled: 0,
    });
    setProjects(projects);
  };

  // Listeners
  useEffect(() => {
    fetchProjects();
    fetchLogs();
    return () => {
      setProjects([]);
      setTotal(0);
      setLogs([]);
    };
  }, []);

  useEffect(() => {
    fetchLogs();
    return () => {
      setTotal(0);
      setLogs([]);
    };
  }, [pageNumber, pageSize]);

  // Actions
  const handleFiltersChange = (value, key) => {
    if (key === "date") value = value.map((date) => date.format("YYYY-MM-DD"));
    setFilters({ ...filters, [key]: value });
    setFiltersUpdated(true);
  };

  const handleFiltersSubmit = () => {
    fetchLogs();
    setFiltersUpdated(false);
  };

  const handleFiltersReset = () => {
    fetchLogs({});
    setFilters({});
    setFiltersUpdated(false);
  };

  const handleNewLogChange = (value, key) => {
    setNewLog({ ...newLog, [key]: value });
  };

  const handleCreation = async () => {
    const data = {
      ...newLog,
      type_id: NOTE_TYPE_LOG,
      status: 1,
      date: new Date(),
      created_by: user.id,
    };

    delete data.documents;

    setUploading(true);
    try {
      const responseLog = await noteService.create(data);
      const responseDoc = await doclist.current.executeActions(responseLog.id, "Note");

      // notification.open({
      //     message: 'Enregistrement effectué',
      //     description: 'Vos changements ont été enregistrés avec succès.',
      //     placement: 'bottomRight'
      // })

      setUploading(false);
      setCreation(false);
      setNewLog(
        canChangeProject ? { project_id: baseProject || null } : { project_id: fixedProject?.id },
      );
      setLogs([responseLog, ...logs]);
      LastProject.set(responseLog?.project_id);
    } catch (e) {
      console.log(e);
      setUploading(false);
      notification.open({
        message: "Une erreur s'est produite",
        description: "Veuillez réessayer ou contacter un administrateur.",
        placement: "bottomRight",
      });
    }
  };

  // Renderers
  const renderFilters = () => (
    <>
      {canChangeProject ? (
        <div className="ctn">
          <label>Projet :</label>
          <Select
            {...selectProps()}
            value={filters["project_id"]}
            style={{ minWidth: 256 }}
            onChange={(val) => handleFiltersChange(val, "project_id")}
          >
            {projects.map((p, i) => (
              <Select.Option key={i} value={p.id}>
                {p.code} : {p.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      ) : (
        <></>
      )}
      <div className="ctn">
        <label>Date :</label>
        <DatePicker.RangePicker
          {...datePickerProps()}
          onChange={(dates) => handleFiltersChange(dates, "date")}
        />
      </div>
      <div className="ctn">
        <label>Créé par :</label>
        <Input
          value={filters.user_name}
          onChange={(e) => handleFiltersChange(e.target.value, "user_name")}
        />
      </div>
      <div className="buttons">
        <Button onClick={handleFiltersReset}>Réinitialiser les filtres</Button>
        <Button type="primary" disabled={!filtersUpdated} onClick={handleFiltersSubmit}>
          Appliquer les filtres
        </Button>
      </div>
    </>
  );

  const renderLogs = () =>
    logs.map((log) => (
      <div key={log.id} className="message-item">
        <UserIcon link user={log.user || {}} />
        <div className="content-wrap">
          <small>
            <span className="name">
              {log?.user?.name} {log?.user?.surname}
            </span>
            <span className="project">{log?.project?.code}</span>
            <span className="date">{moment(log.date).format(dateTimeFormat)}</span>
          </small>
          <p>{log.text}</p>
          <DocumentsList.Preview documents={log.documents} editable={false} />
        </div>
      </div>
    ));

  const renderCreation = () => (
    <Spin
      indicator={<Icon type="loading" spin style={{ fontSize: "52px" }} />}
      spinning={uploading}
    >
      <div className="fields">
        <h3>Nouvelle entrée au journal</h3>
        <div className="ctn">
          <Input.TextArea
            value={newLog.text}
            onChange={(e) => handleNewLogChange(e.target.value, "text")}
          />
        </div>
        <div className="ctn">
          <label>Projet :</label>
          {canChangeProject ? (
            <Select
              {...selectProps()}
              value={newLog.project_id || null}
              onChange={(val) => handleNewLogChange(val, "project_id")}
            >
              {projects.map((p, i) => (
                <Select.Option key={i} value={p.id}>
                  {p.code} : {p.label}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <b>
              {fixedProject?.code} : {fixedProject?.label}
            </b>
          )}
        </div>
        <DocumentsList
          ref={doclist}
          documents={newLog.documents || []}
          editable={true}
          handleChange={handleNewLogChange}
        />
      </div>
      <div className="buttons">
        <Button type="link" onClick={() => setNewLog({})}>
          Annuler
        </Button>
        <Button
          type="primary"
          disabled={!newLog.project_id || !newLog.text}
          onClick={handleCreation}
        >
          Ajouter l'entrée
        </Button>
      </div>
    </Spin>
  );

  return (
    <div className="project-log-wrap">
      <div className="creation-wrap">{renderCreation()}</div>

      <div className={"filters" + (canChangeProject ? " complete" : "")}>{renderFilters()}</div>

      {logs.length === 0 ? (
        <p className="empty">Aucune entrée dans le journal.</p>
      ) : (
        <div className="logs">{renderLogs()}</div>
      )}

      <div className="pagination-container">
        <Pagination
          showSizeChanger
          onShowSizeChange={(page, size) => {
            setPageNumber(page);
            setPageSize(size);
          }}
          onChange={(n) => setPageNumber(n)}
          current={pageNumber}
          total={total}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user.infos || {},
  };
};

export default connect(mapStateToProps, null)(ProjectLog);
